import React from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useNavigate } from "react-router-dom"

import { useAuth, useOperator, usePageReducer } from "hooks"
import { privateRoutes, publicRoutes } from "router/routes"
import { useBikesStore } from "features/dashboard/hooks/useBikesStore"

import { Logo } from "components/Elements/Logos/Logo"
import { BikeIcon, UserIcon, LogoutIcon, QuestionIcon } from "components/Elements"
import "./Header.scoped.scss"

type HeaderProps = {
	className?: string
}

const Header: React.FC<HeaderProps> = ({ className = "" }) => {
	const [isOpen, setIsOpen] = React.useState<boolean>(false)
	const { dispatchPageIsComplete, isComplete } = usePageReducer()
	const { currentOperator, isOperatorComplete } = useOperator()

	const { t } = useTranslation()
	const { logout } = useAuth()
	const navigate = useNavigate()
	const { resetBikes } = useBikesStore()

	const handleLogout = () => {
		// Remove class to prevent bug on signin page
		document.body.classList.remove("fixed", "overflow-y-scroll")
		document.body.style.removeProperty("height")
		logout()
		resetBikes() // reset bikes context after logout
		navigate(publicRoutes.signin.path)
	}

	React.useEffect(() => {
		if (isOpen) {
			document.body.classList.add("fixed", "overflow-y-scroll")
			document.body.style.height = "100vh"
		} else {
			document.body.classList.remove("fixed", "overflow-y-scroll")
			document.body.style.removeProperty("height")
		}
	}, [isOpen])

	React.useEffect(() => {
		if (currentOperator && isOperatorComplete) dispatchPageIsComplete()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOperator])

	return (
		<>
			{isComplete && (
				<header className={`c-header px-4 lg:px-12 py-3 md:py-4 ${className}`}>
					<NavLink to={privateRoutes.dashboard.path}>
						<Logo className="max-w-full c-header__logo" color="var(--primaryColor)" />
					</NavLink>

					<button
						className={`c-header__burger ${isOpen ? "is-active" : ""}`}
						aria-label={isOpen ? t("menu.close-menu") : t("menu.open-menu")}
						onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
					>
						<span></span>
					</button>

					<div className="c-header__overlay"></div>

					<nav
						className={`c-header__nav lg:w-3/5 md:p-0 py-24 lg:pl-10 ml-auto ${
							isOpen ? "has-menu-open" : ""
						}`}
					>
						<NavLink to={privateRoutes.dashboard.path} className="px-2 lg:px-5">
							<BikeIcon size="38" className="mr-2" />
							{t("menu.bikes")}
						</NavLink>
						<NavLink to={privateRoutes.account.path} className="px-2 lg:px-5">
							<UserIcon size="27" className="mr-2" />
							{t("menu.profil")}
						</NavLink>
						<NavLink
							to={publicRoutes.faq.path}
							className="px-2 lg:ml-auto lg:mt-0 flex justify-center"
						>
							<QuestionIcon size="27" className="mr-2" />
							{t("menu.faq")}
						</NavLink>

						<button
							onClick={handleLogout}
							className="flex items-center md:pl-12 text-secondary"
						>
							<LogoutIcon color="var(--secondaryColor)" className="mr-3" />
							<span className="md:hidden lg:block">{t("menu.logout")}</span>
						</button>
					</nav>
				</header>
			)}
		</>
	)
}

export default Header
